import { Consent } from '@axo/form/feature/axo-form-v2/lib/components';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { getFormattedAmountUI } from '@axo/shared/util/formatters';
import { Button } from '@axo/ui-core/components/Button';
import { Icon } from '@axo/ui-core/components/Icon';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Separator } from '@axo/ui-core/components/Separator';
import { Heading, Text } from '@axo/ui-core/components/typography';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import {
  getMarketingConsentDefaultValues,
  useMarketingConsent,
} from '../../../../../hooks/useMarketingConsent';
import { calculateMonthlyAnnuity } from '../../../../../utils/Norway/calculateRates';
import { useEmailValidation } from '../../../../../utils/useEmailValidation';
import { PreFormViewProps } from '../../../../FormProps.types';
import {
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_LOAN_DURATION,
  initialPurposeOptions,
  RATES,
  STARTUP_FEE,
} from '../../config/globals';
import { LoanDurationField } from '../../fields';
import { Step1Data } from '../../FormData.no.types';
import styles from '../../loan.module.scss';
import { getEmailRegisterOptions, phoneFieldOptions } from '../shared-config';
import {
  appliedAmountFieldOptions,
  initialPurposeFieldOptions,
} from './config';
import { USPsNO } from './USPsNO';

const touchedFieldsOverride: Array<keyof Step1Data> = [
  'LoanApplication/AppliedAmount',
  'LoanApplication/LoanDuration',
];

const getDefaultValues = ({
  refinancing,
  loanAmount,
  loanDuration,
  marketingConsent,
  showMarketingConsent,
}: {
  refinancing?: boolean;
  loanAmount?: number;
  loanDuration?: number;
  marketingConsent?: boolean;
  showMarketingConsent?: boolean;
}): Step1Data => ({
  'LoanApplication/InitialPurpose': refinancing
    ? Purpose.RefinancingLoan
    : Purpose.Other,
  'LoanApplication/AppliedAmount': loanAmount || DEFAULT_LOAN_AMOUNT,
  'LoanApplication/LoanDuration': loanDuration || DEFAULT_LOAN_DURATION,
  'Customer/Email': '',
  'Customer/MobilePhoneNumber': '',
  ...getMarketingConsentDefaultValues({
    marketingConsent,
    showMarketingConsent,
    defaultValueOverride: false,
  }),
});

interface Props {
  isPre?: boolean;
  heading?: string;
  options?: PreFormViewProps['options'];
}

export const Step1 = ({ isPre, options }: Props) => {
  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(false);

  const { baseUrl, onChange, onSubmit, onPreSubmit, axoFormData } =
    useAxoFormContext<Step1Data>();

  const form = useForm({
    defaultValues: getDefaultValues({
      refinancing: options?.refinancing,
      loanAmount: options?.defaultLoanAmount,
      loanDuration: options?.defaultLoanDuration,
      marketingConsent: axoFormData['Ignore/MarketingConsent'],
      showMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
    }),
    mode: 'onBlur',
  });

  const {
    watch,
    setValue,
    setError,
    formState: { isSubmitting, isSubmitSuccessful, touchedFields, dirtyFields },
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const loading = isSubmitting || isSubmitSuccessful;

  const showMoreFields =
    showMore ||
    axoFormData['Customer/Email'] ||
    touchedFields['LoanApplication/InitialPurpose'] ||
    dirtyFields['LoanApplication/InitialPurpose'];

  const { showMarketingConsent = true } = useMarketingConsent({
    marketingConsent: axoFormData['Ignore/MarketingConsent'],
    shouldShowMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
  });

  const { EmailPrompt, isValid, formError, enableEmailValidation } =
    useEmailValidation({
      baseUrl,
      email: watch('Customer/Email'),
      setEmail: (email) => setValue('Customer/Email', email),
    });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const [appliedAmount, loanDuration, initialPurpose] = watch([
    'LoanApplication/AppliedAmount',
    'LoanApplication/LoanDuration',
    'LoanApplication/InitialPurpose',
  ]);

  const monthlyPayment = calculateMonthlyAnnuity({
    principal: appliedAmount + STARTUP_FEE,
    annualNominalRate: RATES.MID,
    months: loanDuration * 12,
    round: true,
  });

  const _onSubmit = async (data: Step1Data) => {
    if (!isValid) {
      setError('Customer/Email', { message: 'Email.error.pattern' });
      return;
    }

    if (isPre) {
      onPreSubmit(data);
      return;
    }

    onSubmit(data);
  };

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={_onSubmit}
        onChange={onChange}
        /* We need to override the touched attribute for this for it to trigger a change immediately when it is interacted with.
         Otherwise, the user would need to interact with another field before the change is triggered.*/
        touchedFieldsOverride={touchedFieldsOverride}
      >
        <div className={styles.appliedAmount}>
          <DropdownFieldUI<Step1Data>
            name="LoanApplication/AppliedAmount"
            label={t('AppliedAmount.label', { context: initialPurpose })}
            data-testid="applied-amount-input"
            {...appliedAmountFieldOptions}
          />

          <Stack
            direction="h"
            align="stretch"
            className={styles.monthlyPayment}
          >
            <Text size="l">
              <b>{t('monthly_payment')}</b>
            </Text>
            <Heading size="m" as="span">
              {getFormattedAmountUI(monthlyPayment)} {t('suffix.discount')}
            </Heading>
          </Stack>
        </div>

        <Separator className={styles.separator} color={'light'} />

        <Stack className={styles.initialPurpose} gap={'4xs'}>
          <RadioFieldUI<Step1Data>
            name="LoanApplication/InitialPurpose"
            label={t('InitialPurpose.label')}
            data-testid="refinancing-radio"
            radioStyle="both"
            options={initialPurposeOptions}
            {...initialPurposeFieldOptions}
          />
        </Stack>

        {showMoreFields ? (
          <>
            <LoanDurationField />

            <PhoneFieldUI<Step1Data>
              name="Customer/MobilePhoneNumber"
              label={t('MobilePhoneNumber.label')}
              data-testid="phone-number-input"
              format="*** ** ***"
              {...phoneFieldOptions}
            />

            <EmailFieldUI<Step1Data>
              name="Customer/Email"
              label={t('Email.label')}
              data-testid="email-input"
              registerOptions={emailRegisterOptions}
              error={formError}
              confirmationPrompt={<EmailPrompt />}
            />

            <Consent showMarketingConsent={showMarketingConsent}>
              <Consent.TermsAndConditions />
              <Consent.Marketing />
            </Consent>

            <Navigation.Continue loading={loading} size={isPre ? 'xl' : 'l'}>
              {t('continue')}
            </Navigation.Continue>
          </>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="xl"
            data-testid="button-show-more"
            onClick={() => setShowMore(true)}
            suffix={<Icon name="arrow-right" />}
          >
            {t('continue')}
          </Button>
        )}

        {isPre && <USPsNO />}
      </Form>
    </FormProvider>
  );
};
